import React, { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { Plus, CheckCircle2, Trash2, CalendarClock } from 'lucide-react'
import { accountService, billService } from '../lib/api'
import LoadingSpinner from '../components/LoadingSpinner'
import { formatRupees } from '../utils/currency'

const frequencyOptions = ['daily', 'weekly', 'monthly', 'quarterly', 'yearly']

const BillsPage = () => {
  const [loading, setLoading] = useState(true)
  const [bills, setBills] = useState([])
  const [summary, setSummary] = useState({ monthly_projection: 0, overdue_count: 0, count: 0 })
  const [accounts, setAccounts] = useState([])
  const [showAddModal, setShowAddModal] = useState(false)

  const [formData, setFormData] = useState({
    title: '',
    category: 'Bills',
    amount: '',
    frequency: 'monthly',
    next_due_date: '',
    account_id: '',
    reminder_days_before: 3,
    auto_add_expense: true,
    notes: '',
  })

  const accountMap = useMemo(() => {
    return accounts.reduce((acc, item) => {
      acc[item.id] = item
      return acc
    }, {})
  }, [accounts])

  const loadData = async () => {
    try {
      setLoading(true)
      const [billData, accountData] = await Promise.all([
        billService.getBills({ include_payments: false }),
        accountService.getAccounts(),
      ])

      setBills(billData.bills || [])
      setSummary(billData.summary || { monthly_projection: 0, overdue_count: 0, count: 0 })
      setAccounts(accountData.accounts || [])
    } catch (error) {
      toast.error(error.message || 'Failed to load bills')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  const handleCreateBill = async (e) => {
    e.preventDefault()

    try {
      await billService.createBill({
        ...formData,
        amount: Number(formData.amount),
        account_id: formData.account_id || null,
      })
      toast.success('Bill added')
      setShowAddModal(false)
      setFormData({
        title: '',
        category: 'Bills',
        amount: '',
        frequency: 'monthly',
        next_due_date: '',
        account_id: '',
        reminder_days_before: 3,
        auto_add_expense: true,
        notes: '',
      })
      await loadData()
    } catch (error) {
      toast.error(error.message || 'Failed to create bill')
    }
  }

  const handlePayBill = async (bill) => {
    const amount = window.prompt(`Confirm amount to pay for ${bill.title}`, bill.amount)
    if (!amount) {
      return
    }

    try {
      await billService.payBill(bill.id, { amount: Number(amount) })
      toast.success('Bill paid and expense recorded')
      await loadData()
    } catch (error) {
      toast.error(error.message || 'Failed to pay bill')
    }
  }

  const handleDeleteBill = async (billId) => {
    if (!window.confirm('Deactivate this bill?')) {
      return
    }

    try {
      await billService.deleteBill(billId)
      toast.success('Bill deactivated')
      await loadData()
    } catch (error) {
      toast.error(error.message || 'Failed to deactivate bill')
    }
  }

  if (loading) {
    return <LoadingSpinner message="Loading bills..." />
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Bills & Subscriptions</h1>
          <p className="text-gray-600 dark:text-gray-400">Track due dates, automate bill entries, and avoid late payments.</p>
        </div>

        <button
          onClick={() => setShowAddModal(true)}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Add Bill
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card">
          <p className="text-sm text-gray-500 dark:text-gray-400">Active Bills</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{summary.count || bills.length}</p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-500 dark:text-gray-400">Monthly Projection</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{formatRupees(summary.monthly_projection || 0)}</p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-500 dark:text-gray-400">Overdue</p>
          <p className="text-2xl font-bold text-red-600 dark:text-red-400">{summary.overdue_count || 0}</p>
        </div>
      </div>

      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Upcoming & Active Bills</h2>
        {bills.length === 0 ? (
          <p className="text-gray-500 dark:text-gray-400">No bills configured yet.</p>
        ) : (
          <div className="space-y-3">
            {bills.map((bill) => {
              const dueDate = new Date(bill.next_due_date)
              const isOverdue = dueDate < new Date(new Date().toDateString())

              return (
                <div
                  key={bill.id}
                  className="p-4 rounded-lg bg-gray-50 dark:bg-gray-700 flex items-center justify-between"
                >
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-white">{bill.title}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-300 capitalize">
                      {bill.category} • {bill.frequency}
                    </p>
                    <div className="mt-2 flex items-center gap-2 text-xs">
                      <span className={`px-2 py-1 rounded-full ${isOverdue
                        ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200'
                        : 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200'
                      } inline-flex items-center gap-1`}>
                        <CalendarClock className="h-3 w-3" />
                        Due: {dueDate.toLocaleDateString()}
                      </span>
                      {bill.account_id && (
                        <span className="px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">
                          {accountMap[bill.account_id]?.name || 'Linked account'}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <p className="font-bold text-gray-900 dark:text-white">{formatRupees(bill.amount)}</p>
                    <button
                      onClick={() => handlePayBill(bill)}
                      className="text-emerald-600 hover:text-emerald-700"
                      title="Mark as paid"
                    >
                      <CheckCircle2 className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDeleteBill(bill.id)}
                      className="text-red-600 hover:text-red-700"
                      title="Deactivate bill"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-md p-6">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Create Bill</h3>
            <form onSubmit={handleCreateBill} className="space-y-4">
              <input
                required
                className="input-field"
                placeholder="Bill title"
                value={formData.title}
                onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
              />

              <input
                required
                type="number"
                step="0.01"
                min="0"
                className="input-field"
                placeholder="Amount"
                value={formData.amount}
                onChange={(e) => setFormData((prev) => ({ ...prev, amount: e.target.value }))}
              />

              <input
                className="input-field"
                placeholder="Category"
                value={formData.category}
                onChange={(e) => setFormData((prev) => ({ ...prev, category: e.target.value }))}
              />

              <select
                className="input-field"
                value={formData.frequency}
                onChange={(e) => setFormData((prev) => ({ ...prev, frequency: e.target.value }))}
              >
                {frequencyOptions.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>

              <input
                required
                type="date"
                className="input-field"
                value={formData.next_due_date}
                onChange={(e) => setFormData((prev) => ({ ...prev, next_due_date: e.target.value }))}
              />

              <select
                className="input-field"
                value={formData.account_id}
                onChange={(e) => setFormData((prev) => ({ ...prev, account_id: e.target.value }))}
              >
                <option value="">No account link</option>
                {accounts.map((acc) => (
                  <option key={acc.id} value={acc.id}>{acc.name}</option>
                ))}
              </select>

              <input
                className="input-field"
                type="number"
                min="0"
                placeholder="Reminder days before due"
                value={formData.reminder_days_before}
                onChange={(e) => setFormData((prev) => ({ ...prev, reminder_days_before: Number(e.target.value) }))}
              />

              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={formData.auto_add_expense}
                  onChange={(e) => setFormData((prev) => ({ ...prev, auto_add_expense: e.target.checked }))}
                />
                Auto-create expense on due date
              </label>

              <textarea
                className="input-field"
                rows={2}
                placeholder="Notes"
                value={formData.notes}
                onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
              />

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary flex-1">Create Bill</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default BillsPage
